import React, { memo } from 'react'
import PropTypes from 'prop-types'
import { Map } from 'immutable'
import CheckBox from '@aytm/lib/rfd-ui/components/basic/check-box'
import { formatPrice } from '../functions'
import style from './ShelfFooter.module.scss'
import ScaleButtons from './ScaleButtons'

const ShelfFooter = props => {
  const {
    scale,
    cartCount,
    cartTotal,
    innerWidth,
    na,
    questionSchema,
    showCart,
    theme,
    locale,
    preview,
    generateImage,
    renderScrolling,
    changeNA,
    clearCart,
    showHideCart,
    renderScroll,
    updateScale,
    scaleButtonsRef
  } = props

  // Theme and device flags
  const isMobile = theme === 'mobile' && innerWidth < 460
  const isMobileTheme = theme === 'mobile'
  const isPreviewMobile = preview && isMobileTheme

  // Question schema flags
  const isEcommerce = questionSchema.get('store') === 'ecommerce'
  const hasFullscreen = questionSchema.get('fullscreen')
  const hidePrice = questionSchema.get('hide_price')

  const isFindabilityMode =
    questionSchema.get('mode_type') === 'find' ||
    (questionSchema.get('max_cart_size') === 1 && !questionSchema.get('shopping_cart'))

  // Feature visibility flags
  const shouldShowCart = questionSchema.get('shopping_cart') && !isFindabilityMode
  const shouldShowNa =
    questionSchema.get('na') &&
    !showCart &&
    (!showCart || (hasFullscreen && !generateImage && !isPreviewMobile) || isMobileTheme)
  const shouldRenderLinks = isEcommerce || !hasFullscreen || isPreviewMobile || isMobileTheme
  const shouldShowScaleButtons = questionSchema.get('zoom') && !isMobile && !isEcommerce && !generateImage && !showCart
  const isFullscreenMode = hasFullscreen && !isPreviewMobile && !isMobileTheme && !generateImage && !isEcommerce

  // N/A
  const naAnswer = questionSchema.get('answers').find(answer => answer.get('na'))

  return (
    <div
      className={`
        ${style.shelfFooter}
        ${isMobile ? style.shelfFooterMobile : ''}
        ${isMobile && naAnswer && !showCart ? style.shelfFooterMobileNa : ''}
        ${isMobile && showCart ? style.shelfFooterMobileNaCartOpen : ''}
      `}
      data-testid="SW-SH-FooterLinks"
    >
      {/* N/A */}
      {shouldShowNa && (
        <div className={style.NAButton} data-testid="SW-SH-NA">
          <CheckBox label={naAnswer?.get('text') || locale.getIn(['sh', 'na'])} onAction={changeNA} checked={na} />
        </div>
      )}

      {/* Cart controls and scale buttons */}
      {!generateImage && (
        <>
          {renderScrolling && renderScroll()}
          <div data-testid="SW-SH-Cart" className={style.cart}>
            {/* Cart action buttons */}
            {shouldShowCart && !na && (
              <>
                {shouldRenderLinks && (
                  <button
                    className={style.clear}
                    disabled={!cartCount}
                    data-testid="SW-SH-ClearCart"
                    onClick={clearCart}
                  >
                    {locale.getIn(['sh', 'clearCart'])}
                  </button>
                )}

                {showCart && shouldRenderLinks && (
                  <button className={style.back} onClick={showHideCart} data-testid="SW-SH-HideCart">
                    {locale.getIn(['sh', 'backToStore'])}
                  </button>
                )}
              </>
            )}

            {/* Scale buttons */}
            {shouldShowScaleButtons && !na && (
              <ScaleButtons
                ref={scaleButtonsRef}
                fullscreen={isFullscreenMode}
                scale={scale}
                updateScale={updateScale}
              />
            )}
          </div>
        </>
      )}

      {/* Cart total button */}
      {shouldShowCart && !na && (
        <button
          data-testid="SW-SH-Total"
          onClick={showCart ? null : showHideCart}
          disabled={showCart}
          className={`${style.total} ${hidePrice ? style.hidePrice : ''}`}
        >
          <b data-testid="SW-SH-CartName" className={style.cartName}>
            {`${locale.getIn(['sh', 'total'])}:`}
          </b>

          <div
            dangerouslySetInnerHTML={{
              __html: `${cartCount} ${locale.getIn(['sh', 'items'])}`
            }}
            data-testid="SW-SH-CartCount"
          />

          {!hidePrice && (
            <b
              className={style.totals}
              data-testid="SW-SH-Money"
              dangerouslySetInnerHTML={{
                __html: formatPrice(cartTotal, questionSchema.get('price_mask'), true)
              }}
            />
          )}
        </button>
      )}
    </div>
  )
}

ShelfFooter.propTypes = {
  scale: PropTypes.number,
  cartCount: PropTypes.number,
  cartTotal: PropTypes.number,
  innerWidth: PropTypes.number,
  na: PropTypes.bool,
  showCart: PropTypes.bool,
  theme: PropTypes.string,
  preview: PropTypes.bool,
  generateImage: PropTypes.bool,
  renderScrolling: PropTypes.bool,
  questionSchema: PropTypes.instanceOf(Map).isRequired,
  locale: PropTypes.instanceOf(Map).isRequired,
  changeNA: PropTypes.func,
  clearCart: PropTypes.func,
  showHideCart: PropTypes.func,
  renderScroll: PropTypes.func,
  renderScaleButtons: PropTypes.func,
  updateScale: PropTypes.func,
  scaleButtonsRef: PropTypes.func
}

export default memo(
  ShelfFooter,
  (prevProps, nextProps) =>
    prevProps.scale === nextProps.scale &&
    prevProps.cartCount === nextProps.cartCount &&
    prevProps.cartTotal === nextProps.cartTotal &&
    prevProps.innerWidth === nextProps.innerWidth &&
    prevProps.na === nextProps.na &&
    prevProps.questionSchema === nextProps.questionSchema &&
    prevProps.showCart === nextProps.showCart &&
    prevProps.theme === nextProps.theme &&
    prevProps.locale === nextProps.locale &&
    prevProps.preview === nextProps.preview &&
    prevProps.generateImage === nextProps.generateImage &&
    prevProps.renderScrolling === nextProps.renderScrolling &&
    prevProps.changeNA === nextProps.changeNA &&
    prevProps.clearCart === nextProps.clearCart &&
    prevProps.showHideCart === nextProps.showHideCart &&
    prevProps.renderScroll === nextProps.renderScroll &&
    prevProps.renderScaleButtons === nextProps.renderScaleButtons &&
    prevProps.updateScale === nextProps.updateScale
)
